import { useEffect, useRef, useState } from "react";
import { CheckCircle2, ShieldCheck, X } from "lucide-react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  IconButton,
  Typography,
  alpha,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { fetcher } from "src/services/config";
import { MethodType } from "src/services/type";
import { openToast } from "@utils/toastStore";

interface ResetTotpProps {
  open: boolean;
  onClose: () => void;
  corporateId: string;
  idUser: string | number;
}

export default function ResetTotp({
  open,
  onClose,
  corporateId,
  idUser,
}: ResetTotpProps) {
  const { t } = useTranslation("users-groups-v2");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successfullyReset, setSuccessfullyReset] = useState(false);
  const pending = useRef(false);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const handleReset = async () => {
    if (
      pending.current ||
      successfullyReset ||
      !corporateId ||
      idUser == null ||
      idUser === ""
    ) {
      return;
    }
    pending.current = true;
    setIsSubmitting(true);
    try {
      await fetcher(
        `/usermgmt/admin/corporates/${encodeURIComponent(corporateId)}/users/${encodeURIComponent(String(idUser))}/mfa/totp/reset`,
        MethodType.POST,
      );
      if (mounted.current) setSuccessfullyReset(true);
    } catch {
      if (mounted.current) {
        openToast({
          severity: "error",
          message: t("confirm-dialog.error"),
        });
      }
    } finally {
      pending.current = false;
      if (mounted.current) setIsSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={isSubmitting ? undefined : onClose}
      fullWidth
      maxWidth={false}
      aria-labelledby="reset-totp-title"
      aria-describedby="reset-totp-description"
      PaperProps={{
        sx: (theme) => ({
          width: "min(800px, calc(100vw - 32px))",
          m: 2,
          bgcolor: "background.default",
          color: "text.primary",
          border: `2px solid ${theme.palette.divider}`,
          borderRadius: { xs: 2, md: "18px" },
          boxShadow: "none",
        }),
      }}
    >
      <Box
        sx={{
          position: "relative",
          width: "100%",
          px: { xs: 2, md: 3.5 },
          pt: successfullyReset ? 3.5 : { xs: 8, md: 10 },
          pb: { xs: 3, md: 3.5 },
          display: "flex",
          gap: 2,
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        {!successfullyReset && (
          <IconButton
            aria-label={t("authentication-modal.close")}
            onClick={onClose}
            disabled={isSubmitting}
            sx={{
              position: "absolute",
              top: { xs: 12, md: 24 },
              right: { xs: 12, md: 24 },
              color: "text.primary",
            }}
          >
            <X size={24} />
          </IconButton>
        )}
        {successfullyReset ? (
          <CheckCircle2 size={48} aria-hidden="true" />
        ) : (
          <ShieldCheck size={48} aria-hidden="true" />
        )}
        <Typography
          id="reset-totp-title"
          component="h2"
          aria-live="polite"
          sx={{
            fontSize: { xs: "1.25rem", md: "1.5rem" },
            fontWeight: 700,
            textAlign: "center",
          }}
        >
          {t(
            successfullyReset
              ? "authentication-modal.success-title"
              : "authentication-modal.title",
          )}
        </Typography>
        <Typography
          id="reset-totp-description"
          sx={{
            maxWidth: 604,
            fontSize: { xs: "0.875rem", md: "1rem" },
            lineHeight: 1.2,
            textAlign: "center",
          }}
        >
          {t(
            successfullyReset
              ? "authentication-modal.success-description"
              : "authentication-modal.description",
          )}
          <br />
          {t(
            successfullyReset
              ? "authentication-modal.success-reconfigure"
              : "authentication-modal.reconfigure",
          )}
        </Typography>
        <Box
          sx={(theme) => ({
            width: "100%",
            mt: 1,
            pt: 3,
            display: "flex",
            justifyContent: "center",
            borderTop: `1px solid ${alpha(theme.palette.text.primary, 0.32)}`,
          })}
        >
          {successfullyReset ? (
            <Button
              id="reset-totp-close-button"
              variant="contained"
              size="medium"
              onClick={onClose}
              autoFocus
              sx={{ fontSize: "14px", fontWeight: 700 }}
            >
              {t("authentication-modal.close")}
            </Button>
          ) : (
            <Button
              id="reset-totp-button"
              variant="contained"
              size="medium"
              onClick={handleReset}
              disabled={isSubmitting}
              aria-busy={isSubmitting}
              startIcon={
                isSubmitting ? (
                  <CircularProgress size={16} color="inherit" />
                ) : undefined
              }
              sx={{ fontSize: "14px", fontWeight: 700 }}
            >
              {t("authentication-modal.reset")}
            </Button>
          )}
        </Box>
      </Box>
    </Dialog>
  );
}
